import React, {useState} from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import Button from "@mui/material/Button";

// Human-readable format descriptions for the "Supported formats" panel
const SUPPORTED_FORMATS_INFO = {
    audio: {
        containers: 'WAV, MP3, AIFF, M4A, FLAC, OGG',
        codecs: 'AAC, PCM, OPUS',
    },
    video: {
        containers: 'MP4, M4V, MOV, WEBM',
        codecs: 'H.264, HEVC, VP9, VP8',
        note: 'ProRes and other professional codecs are not supported',
    },
    image: {
        formats: 'BMP, JPEG, PNG, GIF, WEBP',
    },
    serverProcessed: {
        containers: 'AVI, MP4, M4V',
        codecs: 'DIVX and browser-incompatible video codecs',
        note: 'These files are converted on the server before they can be used.',
    },
};

export interface UnsupportedFile {
    name: string;
    extension: string;
}

interface UnsupportedFilesDialogProps {
    isOpen: boolean;
    onClose: () => void;
    files: UnsupportedFile[];
    processableFiles?: UnsupportedFile[];
    onUploadProcessable?: () => Promise<void> | void;
}

export const UnsupportedFilesDialog: React.FC<UnsupportedFilesDialogProps> = ({
    isOpen,
    onClose,
    files,
    processableFiles,
    onUploadProcessable,
}) => {
    const [showSupportedFormats, setShowSupportedFormats] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const rejectedCount = files?.length ?? 0;
    const processableCount = processableFiles?.length ?? 0;

    const subtitle = (() => {
        if (processableCount > 0 && rejectedCount > 0) {
            return 'Some files need server processing. Others aren’t supported.';
        }
        if (processableCount > 1) { // Plural
            return 'These files can be imported, but they need server processing before you can use them.';
        }
        if (processableCount === 1) { // Singular
            return 'This file can be imported, but it needs server processing before you can use it.';
        }
        return `The following ${rejectedCount === 1 ? 'file is' : 'files are'} not supported and ${rejectedCount === 1 ? 'was' : 'were'} not uploaded.`;
    })();

    const handleUploadProcessable = async () => {
        if (!onUploadProcessable) return;
        if (isUploading) return;
        setIsUploading(true);
        try {
            await onUploadProcessable();
        } catch (e) {
            console.warn('[UnsupportedFilesDialog] Failed to upload server-processed files', e);
        } finally {
            setIsUploading(false);
        }
    };

    const textForPlural = "We’ll upload and convert these files on the server. They’ll be usable once they’re ready."
    const textForSingular = "We’ll upload and convert this file on the server. It’ll be usable once it’s ready."

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: '420px',
                    padding: '20px 24px',
                    borderRadius: '12px',
                    boxShadow: '0px 20px 60px rgba(15, 23, 42, 0.08)',
                }
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography sx={{ 
                    fontFamily: 'Inter',
                    fontSize: '15px',
                    fontWeight: 500,
                    lineHeight: '22px',
                    color: '#1A1A1A',
                }}>
                    Review files
                </Typography>
                <IconButton 
                    size="small" 
                    onClick={onClose}
                    sx={{ 
                        color: '#9CA3AF',
                        padding: '4px',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                    }}
                >
                    <Box component="span" sx={{ fontSize: '18px', lineHeight: 1 }}>×</Box>
                </IconButton>
            </Box>
            
            {/* Subtitle */}
            <Typography sx={{ 
                fontFamily: 'Inter',
                fontSize: '13px',
                fontWeight: 400,
                color: '#6B7280',
                mb: 2,
                lineHeight: '18px',
            }}>
                {subtitle}
            </Typography>

            {processableCount > 0 && (
                <Box sx={{ mb: rejectedCount > 0 ? 2 : 1.5 }}>
                    <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', fontWeight: 600, color: '#1A1A1A', mb: 0.75 }}>
                        Needs processing
                    </Typography>
                    <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', color: '#6B7280', lineHeight: '16px', mb: 1.25 }}>
                        {processableCount > 1 ? textForPlural : textForSingular}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '180px', overflowY: 'auto' }}>
                        {processableFiles?.map((file, index) => (
                            <Box
                                key={`processable-${index}`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    padding: '8px 12px',
                                    backgroundColor: 'rgba(43, 107, 253, 0.06)',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(43, 107, 253, 0.18)',
                                }}
                            >
                                <CloudUploadOutlinedIcon sx={{ fontSize: 18, color: '#2B6BFD', flexShrink: 0 }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: 'Inter',
                                            fontSize: '13px',
                                            fontWeight: 500,
                                            color: '#1A1A1A',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {file.name}
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily: 'Inter',
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        color: '#6B7280',
                                        backgroundColor: '#F3F4F6',
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        flexShrink: 0,
                                    }}
                                >
                                    {file.extension}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {rejectedCount > 0 && (
                <Box sx={{ mb: 2 }}>
                    {processableCount > 0 && (
                        <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', fontWeight: 600, color: '#1A1A1A', mb: 0.75 }}>
                            Not supported
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '180px', overflowY: 'auto' }}>
                        {files.map((file, index) => (
                            <Box
                                key={`rejected-${index}`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    padding: '8px 12px',
                                    backgroundColor: '#FEF2F2',
                                    borderRadius: '8px',
                                    border: '1px solid #FECACA',
                                }}
                            >
                                <ErrorOutlineIcon sx={{ fontSize: 18, color: '#EF4444', flexShrink: 0 }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: 'Inter',
                                            fontSize: '13px',
                                            fontWeight: 500,
                                            color: '#1A1A1A',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {file.name}
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily: 'Inter',
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        color: '#6B7280',
                                        backgroundColor: '#F3F4F6',
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        flexShrink: 0,
                                    }}
                                >
                                    {file.extension}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Expandable Supported Formats Section */}
            <Box sx={{ borderTop: '1px solid #E5E7EB', pt: 1.5 }}>
                <Box
                    onClick={() => setShowSupportedFormats(!showSupportedFormats)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        padding: '6px 0',
                        '&:hover': { opacity: 0.8 },
                    }}
                >
                    <Typography sx={{
                        fontFamily: 'Inter',
                        fontSize: '13px',
                        fontWeight: 500,
                        color: '#2B6BFD',
                    }}>
                        View supported formats
                    </Typography>
                    <ExpandMoreIcon 
                        sx={{ 
                            fontSize: 18, 
                            color: '#2B6BFD',
                            transform: showSupportedFormats ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease',
                        }} 
                    />
                </Box>
                
                {showSupportedFormats && (
                    <Box sx={{ 
                        mt: 1.5,
                        padding: '12px',
                        backgroundColor: '#F9FAFB',
                        borderRadius: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                    }}>
                        {/* Audio */}
                        <Box>
                            <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
                                Audio
                            </Typography>
                            <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', color: '#6B7280', lineHeight: '16px' }}>
                                {SUPPORTED_FORMATS_INFO.audio.containers}
                            </Typography>
                            <Typography sx={{ fontFamily: 'Inter', fontSize: '11px', color: '#9CA3AF', lineHeight: '14px' }}>
                                Codecs: {SUPPORTED_FORMATS_INFO.audio.codecs}
                            </Typography>
                        </Box>
                        
                        {/* Video */}
                        <Box>
                            <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
                                Video
                            </Typography>
                            <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', color: '#6B7280', lineHeight: '16px' }}>
                                {SUPPORTED_FORMATS_INFO.video.containers}
                            </Typography>
                            <Typography sx={{ fontFamily: 'Inter', fontSize: '11px', color: '#9CA3AF', lineHeight: '14px' }}>
                                Codecs: {SUPPORTED_FORMATS_INFO.video.codecs}
                            </Typography>
                        </Box>
                        
	                        {/* Image */}
	                        <Box>
	                            <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
	                                Image
	                            </Typography>
	                            <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', color: '#6B7280', lineHeight: '16px' }}>
	                                {SUPPORTED_FORMATS_INFO.image.formats}
	                            </Typography>
	                        </Box>

	                        {/* Server-processed */}
	                        <Box>
	                            <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
	                                Server-processed
	                            </Typography>
	                            <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', color: '#6B7280', lineHeight: '16px' }}>
	                                {SUPPORTED_FORMATS_INFO.serverProcessed.containers}
	                            </Typography>
	                            <Typography sx={{ fontFamily: 'Inter', fontSize: '11px', color: '#9CA3AF', lineHeight: '14px' }}>
	                                Codecs: {SUPPORTED_FORMATS_INFO.serverProcessed.codecs}
	                            </Typography>
	                            <Typography sx={{ fontFamily: 'Inter', fontSize: '11px', color: '#9CA3AF', lineHeight: '14px' }}>
	                                {SUPPORTED_FORMATS_INFO.serverProcessed.note}
	                            </Typography>
	                        </Box>
	                    </Box>
	                )}
	            </Box>

	            {/* Actions */}
	            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
	                {processableCount > 0 ? (
	                    <>
	                        <Button
	                            variant="outlined"
	                            onClick={onClose}
	                            disabled={isUploading}
	                            sx={{
	                                textTransform: 'none',
	                                borderRadius: '10px',
	                                fontFamily: 'Inter',
	                                fontSize: '13px',
	                                fontWeight: 500,
	                                color: '#111111',
	                                borderColor: '#E5E7EB',
	                                '&:hover': {
	                                    borderColor: '#D1D5DB',
	                                    backgroundColor: 'rgba(17, 24, 39, 0.04)',
	                                },
	                            }}
	                        >
	                            Cancel
	                        </Button>
	                        <Button
	                            variant="contained"
	                            disableElevation
	                            onClick={handleUploadProcessable}
	                            disabled={isUploading}
	                            sx={{
	                                textTransform: 'none',
	                                borderRadius: '10px',
	                                fontFamily: 'Inter',
	                                fontSize: '13px',
	                                fontWeight: 500,
	                                backgroundColor: '#111111',
	                                '&:hover': { backgroundColor: '#000000' },
	                            }}
	                        >
	                            {isUploading
	                                ? 'Uploading…'
	                                : `Upload & process${processableCount > 1 ? ` ${processableCount}` : ''}`}
	                        </Button>
	                    </>
	                ) : (
	                    <Button
	                        variant="contained"
	                        disableElevation
	                        onClick={onClose}
	                        sx={{
	                            textTransform: 'none',
	                            borderRadius: '10px',
	                            fontFamily: 'Inter',
	                            fontSize: '13px',
	                            fontWeight: 500,
	                            backgroundColor: '#111111',
	                            '&:hover': { backgroundColor: '#000000' },
	                        }}
	                    >
	                        Got it
	                    </Button>
	                )}
	            </Box>
	        </Dialog>
	    );
	};

export default UnsupportedFilesDialog;
